import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router'
import { ArrowUpRight } from './icons'

/** `inverse`, `ghost-inverse` and `link-inverse` are the on-dark versions; never override hover colours with className, the variant's own hover wins. */
export type ButtonVariant = 'primary' | 'ghost' | 'inverse' | 'ghost-inverse' | 'danger' | 'link' | 'link-inverse' | 'danger-link'
export type ButtonSize = 'md' | 'sm'

type Shared = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Show the brand arrow-up-right icon after the label. Default: true for primary/inverse. */
  arrow?: boolean
  icon?: ReactNode
  children: ReactNode
}

export function buttonClasses({ variant = 'primary', size = 'md', disabled }: { variant?: ButtonVariant; size?: ButtonSize; disabled?: boolean }) {
  return clsx(
    // Icons never give up their width to a long label (every flex child is min-width:0 by default).
    'inline-flex items-center justify-center gap-2 select-none whitespace-nowrap [&>svg]:shrink-0',
    'font-body font-semibold uppercase tracking-button',
    'transition-colors duration-150 ease-out-soft',
    'disabled:cursor-not-allowed disabled:opacity-40',
    // Link variants have no box, so they take no box padding; otherwise px-3.5 can outrank their px-0 and indent them.
    size === 'md' && clsx('text-[0.8125rem]', !variant.includes('link') && 'min-h-11 px-5'),
    size === 'sm' && clsx('text-[0.75rem]', !variant.includes('link') && 'min-h-9 px-3.5'),
    // Hover keeps each variant's shape (filled stays filled, outlined stays outlined) so a primary and a ghost side by
    // side never trade places and it's always clear which one the pointer is on.
    variant === 'primary' && 'bg-eerie text-paper border border-eerie hover:bg-charcoal hover:border-charcoal',
    variant === 'inverse' && 'bg-paper text-ink border border-paper hover:bg-transparent hover:text-paper',
    variant === 'ghost' && 'bg-transparent text-ink border border-ink hover:bg-gray-100',
    variant === 'ghost-inverse' && 'bg-transparent text-paper border border-paper hover:bg-paper hover:text-ink',
    variant === 'danger' && 'bg-transparent text-danger border border-danger hover:bg-danger hover:text-paper',
    variant === 'link' && 'min-h-0 px-0 underline underline-offset-4 decoration-1 text-ink hover:text-dim',
    variant === 'link-inverse' && 'min-h-0 px-0 underline underline-offset-4 decoration-1 text-paper hover:text-paper/70',
    variant === 'danger-link' && 'min-h-0 px-0 underline underline-offset-4 decoration-1 decoration-danger/40 text-danger hover:decoration-danger hover:decoration-2',
    disabled && 'pointer-events-none',
  )
}

function Inner({ children, icon, arrow, variant }: Shared) {
  const showArrow = arrow ?? (variant === 'primary' || variant === 'inverse')
  return (
    <>
      {icon}
      <span>{children}</span>
      {showArrow && <ArrowUpRight size={13} />}
    </>
  )
}

export type ButtonProps = Shared & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>

export function Button({ variant = 'primary', size = 'md', arrow, icon, children, className, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={clsx(buttonClasses({ variant, size, disabled: rest.disabled }), className)} {...rest}>
      <Inner variant={variant} arrow={arrow} icon={icon}>{children}</Inner>
    </button>
  )
}

export type ButtonLinkProps = Shared & Omit<LinkProps, 'children'>

export function ButtonLink({ variant = 'primary', size = 'md', arrow, icon, children, className, ...rest }: ButtonLinkProps) {
  return (
    <Link className={clsx(buttonClasses({ variant, size }), className)} {...rest}>
      <Inner variant={variant} arrow={arrow} icon={icon}>{children}</Inner>
    </Link>
  )
}
